import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import SellFromHome from '../../../assets/images/sell-from-home.png';
import { ReactComponent as ArrowRight } from '../../../assets/images/svg/arrow-right-blue.svg';
import { ReactComponent as Lock } from '../../../assets/images/svg/lock.svg';
import { mediaBelow } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import themeColors from '../../../assets/styles/themeColors';
import useSellFromHomeEligibility from '../../../util/hooks/useSellFromHomeEligibility';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_CTA_SFS_WIZARD_CLICKED,
  SFH_VERIFY_ACCOUNT_CLICKED,
  SFH_VERIFY_ACCOUNT_DEFERRED,
} from '../../../constants/gtmEvents';
import {
  fireSfhInitiationEvent,
  resolveSfhAuthorization,
} from '../../../util/helpers/sfhGtmHelpers';
import { useSfhJourneyStartedEvent } from '../../../util/helpers/crmEventsHelper';
import {
  selectDefaultMailingAddress,
  selectAccountUsersData,
} from '../../../store/selectors/accountSelectors';
import { selectAuthUser } from '../../../store/selectors/userSelectors';
import NonUsSellFromHomeDrawer from './NonUsSellFromHomeDrawer';
import IraUtmaSellFromHomeDrawer from './IraUtmaSellFromHomeDrawer';
import BasicAccountRestristionsModal from '../BasicAccountRestrictionsModal/BasicAccountRestrictionsModal';
import SellFromHomeSsnLockedModal from './SellFromHomeSsnLockedModal';
import PendingAccountVerificationDrawer from '../PendingAccountVerificationDrawer/PendingAccountVerificationDrawer';

const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: ${themeColors.colorBannerBg};
  padding: 12px 16px;
  margin: 36px -16px 0;
  cursor: pointer;
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
`;

const BannerTitle = styled.span`
  font-weight: 600;
  color: ${themeColors.colorLinkPrimary};
  font-size: 16px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const BannerSubtitle = styled.span`
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  font-size: 14px;
`;

const BannerCta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const BannerLockIcon = styled.span`
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 16px;
    color: ${themeColors.colorPrimary};
  }
`;

const BannerCtaLabel = styled.span`
  font-weight: 400;
  color: ${themeColors.colorPrimary};
  font-size: 14px;
  text-decoration: underline;
`;

const SellFromHomeBanner = ({ onOpenSFHWizard }) => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);
  const {
    isGS,
    isNonUS,
    isBasicAccount,
    isIraOrUtmaAccount,
    isJointSubUser,
    isSellFromHomeRestricted,
    isPendingAccount,
  } = useSellFromHomeEligibility();
  const { sfhGtmEvent } = useSfhGtmHook();
  const fireSfhJourneyStarted = useSfhJourneyStartedEvent();
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);
  const authUser = useSelector(selectAuthUser);
  const accountUsers = useSelector(selectAccountUsersData);

  if (!isGS) return null;

  const handleBannerClick = () => {
    sfhGtmEvent(SFH_CTA_SFS_WIZARD_CLICKED, {
      authorization: resolveSfhAuthorization({ authUser, accountUsers }),
    });
    fireSfhJourneyStarted();
    if (isSellFromHomeRestricted) {
      setIsLockedModalOpen(true);
      return;
    }
    if (
      !isNonUS &&
      !isIraOrUtmaAccount &&
      !isJointSubUser &&
      !isBasicAccount &&
      !isPendingAccount &&
      onOpenSFHWizard
    ) {
      onOpenSFHWizard();
      return;
    }
    if (
      !isPendingAccount ||
      isIraOrUtmaAccount ||
      isJointSubUser ||
      isBasicAccount
    ) {
      fireSfhInitiationEvent(sfhGtmEvent, {
        isNonUS,
        isIraOrUtmaAccount,
        isJointSubUser,
        isBasicAccount,
        country: defaultMailingAddress?.CountryIso3Code,
        authorization: resolveSfhAuthorization({ authUser, accountUsers }),
      });
    }
    setIsDrawerOpen(true);
  };

  const renderRestrictionUi = () => {
    if (isIraOrUtmaAccount) {
      return (
        <IraUtmaSellFromHomeDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      );
    }
    if (isJointSubUser) {
      return (
        <IraUtmaSellFromHomeDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          title={t('portfolio.sellFromHome.jointSubUser.title')}
          description={t('portfolio.sellFromHome.jointSubUser.description')}
          dataCyPrefix="button-sfh-joint-sub-user"
        />
      );
    }
    if (isBasicAccount) {
      return (
        <BasicAccountRestristionsModal
          isOpen={isDrawerOpen}
          close={() => setIsDrawerOpen(false)}
          title={t('basicAccountRestrictions.verifyAccountSellFromHome.title')}
          description={t(
            'basicAccountRestrictions.verifyAccountSellFromHome.description',
          )}
          icon={<InfoIcon width={48} height={48} />}
          showDoItLater
          onVerifyAccountClick={() => sfhGtmEvent(SFH_VERIFY_ACCOUNT_CLICKED)}
          onDoItLaterClick={() => sfhGtmEvent(SFH_VERIFY_ACCOUNT_DEFERRED)}
        />
      );
    }
    if (isPendingAccount) {
      return (
        <PendingAccountVerificationDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      );
    }
    if (isNonUS) {
      return (
        <NonUsSellFromHomeDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      );
    }
    return null;
  };

  return (
    <>
      <BannerContainer onClick={handleBannerClick}>
        <BannerLeft>
          <img src={SellFromHome} alt="Sell From Home" width={55} height={35} />
          <BannerText>
            <BannerTitle>{t('sellWizard.sellFromHome.title')}</BannerTitle>
            <BannerSubtitle>
              {t('sellWizard.sellFromHome.subtitle')}
            </BannerSubtitle>
          </BannerText>
        </BannerLeft>
        <BannerCta>
          {isSellFromHomeRestricted && (
            <BannerLockIcon aria-hidden>
              <Lock />
            </BannerLockIcon>
          )}
          <BannerCtaLabel>{t('sellWizard.sellFromHome.cta')}</BannerCtaLabel>
          {!isSellFromHomeRestricted && (
            <ArrowRight fill={themeColors.colorPrimary} />
          )}
        </BannerCta>
      </BannerContainer>

      <SellFromHomeSsnLockedModal
        isOpen={isLockedModalOpen}
        close={() => setIsLockedModalOpen(false)}
      />
      {renderRestrictionUi()}
    </>
  );
};

SellFromHomeBanner.propTypes = {
  onOpenSFHWizard: PropTypes.func,
};

export default SellFromHomeBanner;
